import { useEffect, useState } from "react";
import { Building2, Plus, Search, User, Users, X } from "lucide-react";
import api from "../../lib/api";
import { CLASSIFICATIONS, ORGANISATION_ROLES, PARTY_KIND_LABELS } from "../../lib/records";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const KIND_ICONS = { customer: Building2, collaborator: User, organisation: Users, company: Building2 };

// A pharmacy or a collaborator fiche, looked up by name among those the server would accept (GET /records/party-options).
export function PartySearch({ kind, onPick }) {
  const [search, setSearch] = useState("");
  const [items, setItems] = useState(null);
  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/records/party-options", { params: { kind, ...(search.trim() ? { search: search.trim() } : {}) } });
        setItems(data.items);
      } catch {
        setItems([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [kind, search]);
  return (
    <div className="space-y-2">
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-10" data-testid={`records-party-search-${kind}`}
          placeholder={kind === "customer" ? "Rechercher une pharmacie cliente…" : "Rechercher un collaborateur…"} aria-label="Rechercher une partie" />
      </div>
      {items && items.length === 0 && <p className="text-xs text-slate-500">Aucun résultat.</p>}
      {items && items.length > 0 && (
        <ul className="max-h-48 overflow-y-auto rounded-md border border-slate-200 divide-y divide-slate-100">
          {items.map((item) => (
            <li key={item.ref}>
              <button type="button" onClick={() => onPick({ kind, ref: item.ref, name: item.name })} data-testid="records-party-option"
                className="flex w-full items-baseline justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-slate-50">
                <span className="font-medium text-slate-900">{item.name}</span>
                {item.detail && <span className="text-xs text-slate-500 truncate">{item.detail}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// The parties of the document: pharmacies, collaborators, outside organisations (named here) and Jamora itself.
export function PartiesEditor({ parties, setParties, disabled }) {
  const [adding, setAdding] = useState(parties.length ? "" : "customer");
  const [orgName, setOrgName] = useState("");
  const [orgRole, setOrgRole] = useState("supplier");
  const add = (party) => {
    setParties((list) => {
      const key = (p) => `${p.kind}:${p.ref || p.name.toLocaleLowerCase("fr")}`;
      return list.some((p) => key(p) === key(party)) ? list : [...list, party];
    });
    setAdding("");
    setOrgName("");
  };
  const hasCompany = parties.some((p) => p.kind === "company");
  return (
    <div className="space-y-3" data-testid="records-parties">
      {parties.length > 0 && (
        <ul className="space-y-1.5">
          {parties.map((p, index) => {
            const Icon = KIND_ICONS[p.kind];
            return (
              <li key={`${p.kind}-${p.ref || p.name}`} className="flex items-center gap-3 rounded-md border border-slate-200 px-3 py-2" data-testid="records-party">
                <Icon size={16} className="text-slate-400 shrink-0" aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-900 break-words">{p.name}</div>
                  <div className="text-xs text-slate-500">{p.kind === "organisation" ? ORGANISATION_ROLES[p.role] : PARTY_KIND_LABELS[p.kind]}</div>
                </div>
                <button type="button" disabled={disabled} onClick={() => setParties((list) => list.filter((_, i) => i !== index))} aria-label={`Retirer ${p.name}`}
                  className="flex h-8 w-8 items-center justify-center rounded text-slate-500 hover:bg-slate-100"><X size={15} /></button>
              </li>
            );
          })}
        </ul>
      )}
      {!disabled && (
        <div className="flex flex-wrap gap-2">
          {["customer", "collaborator", "organisation"].map((kind) => (
            <Button key={kind} type="button" size="sm" variant={adding === kind ? "default" : "outline"} onClick={() => setAdding(adding === kind ? "" : kind)}
              data-testid={`records-party-add-${kind}`}>
              <Plus size={14} className="mr-1" />{PARTY_KIND_LABELS[kind]}
            </Button>
          ))}
          {!hasCompany && (
            <Button type="button" size="sm" variant="outline" onClick={() => add({ kind: "company", ref: null, name: "Jamora" })} data-testid="records-party-add-company">
              <Plus size={14} className="mr-1" />Jamora
            </Button>
          )}
        </div>
      )}
      {(adding === "customer" || adding === "collaborator") && <PartySearch kind={adding} onPick={add} />}
      {adding === "organisation" && (
        <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_12rem_auto] sm:items-end">
          <div className="space-y-1.5">
            <Label htmlFor="records-org-name">Nom de l'organisation</Label>
            <Input id="records-org-name" value={orgName} onChange={(e) => setOrgName(e.target.value)} maxLength={180} className="h-10" data-testid="records-org-name" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="records-org-role">Rôle</Label>
            <Select value={orgRole} onValueChange={setOrgRole}>
              <SelectTrigger id="records-org-role" className="h-10"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(ORGANISATION_ROLES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button type="button" disabled={orgName.trim().length < 2} onClick={() => add({ kind: "organisation", ref: null, name: orgName.trim(), role: orgRole })}
            data-testid="records-org-add">Ajouter</Button>
        </div>
      )}
    </div>
  );
}

// Accounts that see the register (GET /records/people): the server refuses anyone else as responsible or reader.
export function usePeople() {
  const [people, setPeople] = useState([]);
  useEffect(() => { api.get("/records/people").then(({ data }) => setPeople(data.items)).catch(() => setPeople([])); }, []);
  return people;
}

export function ClassificationField({ value, onChange, canConfidential }) {
  return (
    <div className="grid gap-2 sm:grid-cols-3" role="radiogroup" aria-label="Confidentialité" data-testid="records-classification">
      {Object.entries(CLASSIFICATIONS).map(([key, c]) => {
        const disabled = key === "confidential" && !canConfidential;
        const active = value === key;
        return (
          <button key={key} type="button" role="radio" aria-checked={active} disabled={disabled} onClick={() => onChange(key)}
            data-testid={`records-classification-${key}-option`}
            className={`rounded-md border px-3 py-2 text-left transition-colors disabled:opacity-50 ${active ? "border-jamora bg-jamora/5" : "border-slate-200 hover:bg-slate-50"}`}>
            <div className="text-sm font-medium text-slate-900">{c.label}</div>
            <div className="text-xs text-slate-500">{disabled ? "Vous ne voyez pas les documents confidentiels." : c.hint}</div>
          </button>
        );
      })}
    </div>
  );
}

export function ReadersPicker({ people, value, onChange, classification }) {
  const [filter, setFilter] = useState("");
  const shown = people.filter((p) => (classification !== "confidential" || p.confidential)
    && p.name.toLocaleLowerCase("fr").includes(filter.trim().toLocaleLowerCase("fr")));
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  return (
    <div className="space-y-2" data-testid="records-readers">
      <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filtrer les personnes…" className="h-9" aria-label="Filtrer les lecteurs" />
      <ul className="max-h-48 overflow-y-auto rounded-md border border-slate-200 divide-y divide-slate-100">
        {shown.length === 0 && <li className="px-3 py-2 text-xs text-slate-500">Aucune personne ne voit le registre.</li>}
        {shown.map((p) => (
          <li key={p.id}>
            <label className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
              <Checkbox checked={value.includes(p.id)} onCheckedChange={() => toggle(p.id)} data-testid="records-reader-option" />{p.name}
            </label>
          </li>
        ))}
      </ul>
      <p className="text-xs text-slate-500">{value.length} lecteur{value.length > 1 ? "s" : ""} nommé{value.length > 1 ? "s" : ""}. Le responsable et le créateur voient toujours le document.</p>
    </div>
  );
}
